import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getDocumentsList } from './prestacaoDeContasActions';
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Link,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DescriptionIcon from '@material-ui/icons/Description';
import FolderIcon from '@material-ui/icons/Folder';
import LoadingProgress from 'common/components/progress/loading';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(2, 0),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
  },
  subtitle: {
    color: '#666',
    lineHeight: 1.6,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
    },
  },
  accordion: {
    marginBottom: theme.spacing(1),
    borderRadius: '8px !important',
    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: theme.spacing(1, 0),
    },
  },
  accordionSummary: {
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    minHeight: '56px',
    '&.Mui-expanded': {
      minHeight: '56px',
      borderRadius: '8px 8px 0 0',
    },
  },
  accordionSummaryContent: {
    alignItems: 'center',
    '&.Mui-expanded': {
      margin: '12px 0',
    },
  },
  yearTitle: {
    fontWeight: 600,
    color: '#333',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  folderIcon: {
    color: '#1976d2',
  },
  chip: {
    marginLeft: theme.spacing(1),
    backgroundColor: '#1976d2',
    color: '#fff',
    fontWeight: 500,
  },
  accordionDetails: {
    padding: theme.spacing(0),
    backgroundColor: '#fff',
  },
  documentList: {
    width: '100%',
    padding: 0,
  },
  documentItem: {
    borderBottom: '1px solid #f0f0f0',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
    '&:last-child': {
      borderBottom: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  documentIcon: {
    minWidth: '40px',
    color: '#d32f2f',
    [theme.breakpoints.down('sm')]: {
      minWidth: '32px',
    },
  },
  documentLink: {
    color: '#1976d2',
    textDecoration: 'none',
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
    },
  },
  emptyMessage: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#666',
  },
}));

const PrestacaoDeContas = ({ getDocumentsList, documentosTransparency }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedPanels, setExpandedPanels] = useState({});

  useEffect(() => {
    getDocumentsList();
  }, [getDocumentsList]);

  const { list, loading } = documentosTransparency;

  const groupedDocuments = list.reduce((acc, documento) => {
    const yearMatch = documento.nome.match(/20\d{2}/);
    const year = yearMatch ? yearMatch[0] : 'Outros';
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(documento);
    return acc;
  }, {});

  const monthOrder = {
    janeiro: 1,
    fevereiro: 2,
    março: 3,
    marco: 3,
    abril: 4,
    maio: 5,
    junho: 6,
    julho: 7,
    agosto: 8,
    setembro: 9,
    outubro: 10,
    novembro: 11,
    dezembro: 12,
  };

  const getMonthIndex = (nome) => {
    const lowerName = nome.toLowerCase();
    for (const [month, index] of Object.entries(monthOrder)) {
      if (lowerName.includes(month)) {
        return index;
      }
    }
    return 13; // documentos sem mês reconhecido vão para o final
  };

  // Ordenar documentos por mês dentro de cada ano
  Object.keys(groupedDocuments).forEach((year) => {
    groupedDocuments[year].sort((a, b) => getMonthIndex(a.nome) - getMonthIndex(b.nome));
  });

  const sortedYears = Object.keys(groupedDocuments).sort((a, b) => b - a);

  useEffect(() => {
    if (sortedYears.length > 0 && Object.keys(expandedPanels).length === 0) {
      setExpandedPanels({ [sortedYears[0]]: true });
    }
  }, [sortedYears]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanels((prev) => ({
      ...prev,
      [panel]: isExpanded,
    }));
  };

  return (
    <section id="prestacao-de-contas" className={classes.root}>
      {loading ? (
        <Box display="flex" height="50vh" justifyContent="center" alignItems="center">
          <LoadingProgress />
        </Box>
      ) : list.length > 0 ? (
        <>
          <Box className={classes.header}>
            <Typography variant="h4" className={classes.title}>
              Prestação de Contas
            </Typography>
            <Typography variant="body1" className={classes.subtitle}>
              O Sindilegis recuperou sua credibilidade junto aos servidores do
              Congresso Nacional e TCU graças à seriedade com que trata o
              patrimônio dos filiados. Acreditamos que a transparência é
              fundamental em qualquer gestão. Por isso, todas as prestações de
              contas a partir de 2014, analisadas pelo Conselho Fiscal,
              encontram-se disponíveis abaixo.
            </Typography>
          </Box>

          {sortedYears.map((year) => (
            <Accordion
              key={`accordion-${year}`}
              expanded={expandedPanels[year] || false}
              onChange={handleAccordionChange(year)}
              className={classes.accordion}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={classes.accordionSummary}
                classes={{ content: classes.accordionSummaryContent }}
              >
                <Typography className={classes.yearTitle}>
                  <FolderIcon className={classes.folderIcon} />
                  Ano de {year}
                </Typography>
                <Chip
                  label={`${groupedDocuments[year].length} ${
                    groupedDocuments[year].length === 1 ? 'documento' : 'documentos'
                  }`}
                  size="small"
                  className={classes.chip}
                />
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <List className={classes.documentList}>
                  {groupedDocuments[year].map((documento, index) => (
                    <ListItem
                      key={`${year}-${documento.nome}-${index}`}
                      className={classes.documentItem}
                    >
                      <ListItemIcon className={classes.documentIcon}>
                        <DescriptionIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          documento.anexo ? (
                            <Link
                              href={documento.anexo.url}
                              target="_blank"
                              rel="noopener"
                              className={classes.documentLink}
                            >
                              {documento.nome}
                            </Link>
                          ) : (
                            documento.nome
                          )
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      ) : (
        <Typography variant="h6" className={classes.emptyMessage}>
          Não há documentos a serem exibidos.
        </Typography>
      )}
    </section>
  );
};

const mapStateToProps = (state) => ({
  documentosTransparency: state.prestacaoDeContas,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ getDocumentsList }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(PrestacaoDeContas);
